/**
 * @file The rows of the selected moment, counted two ways (SYD-2450). Consumers:
 * `FormRecoveryGroup` and `FormRecoveryBanner`, whose two `Restore…` gates have to
 * agree — a moment at which only rows changed leaves the version `unchanged`, so a
 * gate counting the version's own keys alone refuses a restore the operator can
 * plainly see there is work for.
 *
 * `differing` is every row that differs from live; `writable` is the subset the
 * restore would actually write, a locked row being one it is told to leave exactly
 * as it is. The two are separate because `differing > 0` with `writable === 0` is a
 * real state with its own sentence: the moment does not match the form, the restore
 * simply cannot write the rows that differ. A table still being read or refused
 * counts zero on both, which is why the callers hold the gate on the read itself
 * rather than on these numbers alone.
 */

import type { Snapshot } from 'valtio';

import type { RecoveryTableRead } from '../../../store/form/formRecoveryStore';

export const restorableRowCounts = (
  reads: readonly Snapshot<RecoveryTableRead>[],
): { differing: number; writable: number } =>
  reads.reduce(
    (totals, read) => {
      if (read.result?.outcome !== 'version') return totals;
      const { diff } = read.result;
      return {
        differing: totals.differing + diff.length,
        writable: totals.writable + diff.filter((row) => !row.liveLocked).length,
      };
    },
    { differing: 0, writable: 0 },
  );
